// 명세서 삭제 시 수집 케이스 되돌리기 (2026-10-23 거래명세서 1단계 — 계약 §3·§5)
// 초안 하드삭제·발행본 삭제 표시 때 그 명세서에 묶였던 parsedCases(status 'imported' + statementId)를
//   'pending' 으로 되돌려 수집함·작성 화면에 다시 나타나게 한다 ("조용히 청구에서 빠지는" 경로 차단, 명세 §1-1).
// 명세서 쓰기와 케이스 되돌림은 writeBatch 하나로 묶는다(둘 중 하나만 되는 일 방지).
// 실패는 throw — 호출 화면이 알림으로 보여 줄 것. console 에는 id·건수만.
import {
    collection, query, where, getDocs, doc, writeBatch, deleteField, serverTimestamp,
} from 'firebase/firestore';
import { db } from '../firebase/config';

const BATCH_LIMIT = 500;

const stripCasePrefix = (id) => String(id || '').replace(/^case:/, '');
const isLiveIssued = (s) => ['confirmed', 'published'].includes(s.status) && s.deletedBySender !== true;

// 케이스 id → 그 케이스를 항목으로 가진 '살아 있는 발행본' id (지우는 명세서 자신은 제외)
async function liveStatementByCase(labId, exceptStatementId) {
    const snap = await getDocs(query(collection(db, 'transactionStatements'), where('fromUserId', '==', labId)));
    const map = new Map();
    snap.docs.forEach(d => {
        if (d.id === exceptStatementId) return;
        const s = d.data();
        if (!isLiveIssued(s)) return;
        (Array.isArray(s.items) ? s.items : []).forEach(it => {
            if (!it) return;
            const ids = [it.parsedCaseId, it.caseId, String(it.orderId || '').startsWith('case:') ? it.orderId : null]
                .filter(Boolean).map(stripCasePrefix);
            ids.forEach(id => { if (!map.has(id)) map.set(id, d.id); });
        });
    });
    return map;
}

// 이 명세서에 묶인 케이스 (statementId == 그 명세서 id 만). 소장이 [제외]한 건은 되돌리지 않는다.
//   다른 살아 있는 발행본에 이미 들어 있는 케이스는 pending 으로 돌리지 않고 statementId 를 그 발행본으로 옮긴다(이중 청구 방지).
//   keepIds(선택): 지금 문서에 아직 남아 있는 케이스 id — 이들은 건드리지 않는다 (줄 삭제 되돌림용, P1-2).
//   되돌릴 케이스가 0건이면 명세서 전량 조회는 하지 않는다 (2026-10-24 재점검 P1-4 — 읽기 절감).
async function casesOfStatement(statementId, labId, keepIds = null) {
    const snap = await getDocs(query(collection(db, 'parsedCases'), where('labId', '==', labId), where('statementId', '==', statementId)));
    const docs = snap.docs.filter(d => d.data().status !== 'excluded' && !(keepIds && keepIds.has(d.id)));
    if (docs.length === 0) return [];
    const liveMap = await liveStatementByCase(labId, statementId);
    return docs.map(d => ({ ref: d.ref, liveId: liveMap.get(d.id) || null }));
}

// 케이스 되돌림 + 명세서 쓰기 1건. 500건 이하 = 한 번에. 넘으면 케이스를 먼저 나눠 되돌리고 마지막 묶음에 명세서 쓰기
//   (명세서 쓰기가 실패해도 케이스만 pending 이 되어 다시 보일 뿐, 조용히 사라지는 쪽으로는 실패하지 않는다)
async function commitWithRestore(caseRefs, applyStatementOp) {
    const restoreOp = (b, c) => (c.liveId
        ? b.update(c.ref, { status: 'imported', statementId: c.liveId })
        : b.update(c.ref, { status: 'pending', statementId: deleteField() }));
    const perBatch = BATCH_LIMIT - 1;
    let i = 0;
    while (caseRefs.length - i > perBatch) {
        const b = writeBatch(db);
        caseRefs.slice(i, i + perBatch).forEach(c => restoreOp(b, c));
        await b.commit();
        i += perBatch;
    }
    const last = writeBatch(db);
    caseRefs.slice(i).forEach(c => restoreOp(last, c));
    applyStatementOp(last);
    await last.commit();
}

const countRestored = (cs) => cs.filter(c => !c.liveId).length;

// 명세서 줄 → 그 줄이 가리키는 케이스 id (parsedCaseId · caseId · 'case:' 가상 orderId)
export const caseIdsOfItems = (items) => {
    const out = new Set();
    (Array.isArray(items) ? items : []).forEach(it => {
        if (!it) return;
        [it.parsedCaseId, it.caseId, String(it.orderId || '').startsWith('case:') ? it.orderId : null]
            .filter(Boolean).forEach(v => out.add(stripCasePrefix(v)));
    });
    return out;
};

/**
 * 저장·발행 때 "이 문서에 묶였는데(statementId) 지금 줄에는 없는" 케이스 되돌림 (2026-10-24 재점검 P1-2).
 *   초안에서 줄을 지운 뒤 저장·발행하면 그 케이스가 imported 로 영구 잔존하던 경로 차단.
 *   · 지금 items 에 있는 케이스·[제외]된 케이스는 그대로. 정정 원본에 묶인 케이스(statementId == 원본)는 조회 대상이 아니라 건드리지 않는다.
 *   · 다른 살아 있는 발행본에 있는 케이스는 그 발행본으로 리링크, 아니면 pending + statementId 삭제.
 * @returns {Promise<{ restored, relinked }>}  실패는 throw (호출부가 기록)
 */
export async function restoreCasesDroppedFromStatement(statementId, labId, items) {
    if (!statementId || !labId) throw new Error('statementId·labId 없음');
    const cs = await casesOfStatement(statementId, labId, caseIdsOfItems(items));
    if (cs.length === 0) return { restored: 0, relinked: 0 };
    await commitWithRestore(cs, () => {});   // 명세서 쓰기는 호출부가 이미 끝냄 — 케이스만
    const restored = countRestored(cs);
    console.log('[caseRestore] 줄에서 빠진 케이스 되돌림', statementId, '되돌림', restored, '다른 발행본 연결', cs.length - restored);
    return { restored, relinked: cs.length - restored };
}

/** 초안 하드삭제 + 케이스 되돌림 → { restored, relinked } (restored = pending 으로 돌린 수, relinked = 다른 발행본으로 옮긴 수) */
export async function deleteDraftAndRestoreCases(statementId, labId) {
    if (!statementId || !labId) throw new Error('statementId·labId 없음');
    const cs = await casesOfStatement(statementId, labId);
    await commitWithRestore(cs, (b) => b.delete(doc(db, 'transactionStatements', statementId)));
    const restored = countRestored(cs);
    console.log('[caseRestore] 초안 삭제', statementId, '되돌림', restored, '다른 발행본 연결', cs.length - restored);
    return { restored, relinked: cs.length - restored };
}

/**
 * 발행본 삭제 표시 + (정정이 아닐 때만) 케이스 되돌림 → { restored, relinked }
 * @param markFields 기존 3키 { deletedBySender: true, deletedBySenderAt: serverTimestamp(), correctedTo? }
 *   correctedTo 가 있으면 정정본에 이미 들어갔으므로 케이스는 그대로 둔다.
 */
export async function markDeletedAndRestoreCases(statementId, labId, markFields) {
    if (!statementId || !labId) throw new Error('statementId·labId 없음');
    const fields = markFields || { deletedBySender: true, deletedBySenderAt: serverTimestamp() };
    const ref = doc(db, 'transactionStatements', statementId);
    if (fields.correctedTo) {
        const b = writeBatch(db);
        b.update(ref, fields);
        await b.commit();
        console.log('[caseRestore] 정정 삭제 표시', statementId, '되돌림 0');
        return { restored: 0, relinked: 0 };
    }
    const cs = await casesOfStatement(statementId, labId);
    await commitWithRestore(cs, (b) => b.update(ref, fields));
    const restored = countRestored(cs);
    console.log('[caseRestore] 발행본 삭제 표시', statementId, '되돌림', restored, '다른 발행본 연결', cs.length - restored);
    return { restored, relinked: cs.length - restored };
}
